import { useState } from 'react';
import { Outlet, NavLink, Link } from 'react-router-dom';
import { useAuthStore } from '@/core/stores/authStore';
import {
  Home,
  Users,
  FileText,
  DollarSign,
  Calendar,
  Heart,
  Target,
  BookOpen,
  ShoppingBag,
  MessageCircle,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
} from 'lucide-react';

/**
 * FamilyLayout - Main layout for the family portal
 * 
 * Features:
 * - Collapsible sidebar navigation
 * - Top header with user menu
 * - Mobile-responsive design
 * - Active state indicators
 */
export default function FamilyLayout() {
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/family/dashboard', icon: Home },
    { name: 'Children', href: '/family/children', icon: Users },
    { name: 'Health Tracking', href: '/family/health', icon: Heart },
    { name: 'Documents', href: '/family/documents', icon: FileText },
    { name: 'Expenses', href: '/family/expenses', icon: DollarSign },
    { name: 'Calendar', href: '/family/calendar', icon: Calendar },
    { name: 'Care Team', href: '/family/care-team', icon: Users },
    { name: 'Goals', href: '/family/goals', icon: Target },
  ];

  const supportNavigation = [
    { name: 'Resources', href: '/family/resources', icon: BookOpen },
    { name: 'Marketplace', href: '/family/marketplace', icon: ShoppingBag },
    { name: 'Community', href: '/family/community', icon: MessageCircle },
  ];

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-navy/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full bg-cream-dark border-r border-warm-gray/10
          transition-all duration-smooth
          ${sidebarOpen ? 'w-64' : 'w-20'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-warm-gray/10">
          <Link to="/family/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-sage to-sage-dark rounded-xl flex items-center justify-center text-white flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3L20 7.5V16.5L12 21L4 16.5V7.5L12 3Z"/>
                <path d="M12 12L12 21"/>
                <path d="M12 12L20 7.5"/>
                <path d="M12 12L4 7.5"/>
              </svg>
            </div>
            {sidebarOpen && (
              <span className="font-display text-xl font-semibold text-navy">
                Bright<span className="text-sage">Path</span>
              </span>
            )}
          </Link>
          
          {/* Mobile close button */}
          <button 
            className="lg:hidden p-2 rounded-md hover:bg-cream"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {/* Main Menu */}
          <div className="mb-6">
            {sidebarOpen && (
              <p className="px-3 mb-2 text-xs font-semibold text-warm-gray uppercase tracking-wider">
                Main Menu
              </p>
            )}
            <div className="space-y-1">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  end={item.href === '/family/dashboard'}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-md transition-fast
                    ${isActive ? 'bg-sage text-white' : 'text-navy hover:bg-cream'}
                  `}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="font-medium">{item.name}</span>}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Support Section */}
          <div className="mb-6">
            {sidebarOpen && (
              <p className="px-3 mb-2 text-xs font-semibold text-warm-gray uppercase tracking-wider">
                Support
              </p>
            )}
            <div className="space-y-1">
              {supportNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-md transition-fast
                    ${isActive ? 'bg-sage text-white' : 'text-navy hover:bg-cream'}
                  `}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="font-medium">{item.name}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        </nav>

        {/* Sidebar Footer - Collapse Toggle (Desktop) */}
        <div className="hidden lg:flex p-3 border-t border-warm-gray/10">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex items-center justify-center w-full py-2 px-3 rounded-md text-warm-gray hover:bg-cream transition-fast"
          >
            <Menu className="w-5 h-5" />
            {sidebarOpen && <span className="ml-2 text-sm">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`transition-all duration-smooth ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 bg-cream/95 backdrop-blur-md border-b border-warm-gray/10">
          <div className="h-full px-4 lg:px-8 flex items-center justify-between">
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-md hover:bg-cream-dark"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Page title area - can be used by pages */}
            <div className="flex-1" />

            {/* Right side actions */}
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <button className="relative p-2 rounded-md hover:bg-cream-dark transition-fast">
                <Bell className="w-5 h-5 text-navy" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-terracotta rounded-full" />
              </button>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-cream-dark transition-fast"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-sage to-sage-light rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-navy">
                    {user?.first_name}
                  </span>
                  <ChevronDown className="w-4 h-4 text-warm-gray" />
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-medium border border-warm-gray/10 py-1 z-20">
                      <div className="px-4 py-3 border-b border-warm-gray/10">
                        <p className="text-sm font-medium text-navy">{user?.first_name} {user?.last_name}</p>
                        <p className="text-xs text-warm-gray">{user?.email}</p>
                      </div>
                      <Link
                        to="/family/settings"
                        className="flex items-center gap-3 px-4 py-2 text-sm text-navy hover:bg-cream transition-fast"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-terracotta hover:bg-cream transition-fast"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

